// src/components/congressional/TickerStatusBadge.jsx
// Congressional Trade Tracker — Member Page (design doc Section 2, Tab 1).
// Built ahead of the full Trade Feed table since the badge logic is a clean,
// isolated piece — the page itself (route, table, filters) is not built yet.
//
// WHY THIS EXISTS: congressional_trades.tickerResolutionStatus is not always
// 'NATIVE' (a confirmed real ticker). A member-facing page showing every row
// with the same visual confidence regardless of that status would be a real
// accuracy exposure — a PENDING_REVIEW candidate ticker that later turns out
// wrong looks, today, identical to a confirmed one. No competitor (Capitol
// Trades, Quiver Quant, TraderCongress, Unusual Whales) surfaces this
// distinction at all — this is a real differentiator, not just a caveat.
//
// FOUR STATES HANDLED, DELIBERATELY NOT CONFLATED:
//   NATIVE           — confirmed real ticker. No badge at all (the default,
//                       unmarked case — most rows).
//   PENDING_REVIEW   — a Tier 3/4 fuzzy or Yahoo-fallback candidate exists
//                       (tickerResolver.js) but has NOT been human-confirmed
//                       via the Review Console yet. Amber, "under review."
//   ROTATION_SIGNAL  — ticker='--' is the DATA, not a gap — a genuine
//                       Treasury/bond rotation signal (standing project rule:
//                       "never filter these out"). Deliberately a DIFFERENT
//                       color/copy than PENDING_REVIEW/UNRESOLVED so it is
//                       never read as a data-quality problem — it isn't one.
//   NON_EQUITY       — crypto/commodity holding. sector_mapping is SEC-equity-
//                       sourced and structurally can never resolve these
//                       (see tickerResolver.js's own exclusion comment) — not
//                       an error, a category fact.
//   UNRESOLVED       — nothing found anywhere, including the Yahoo fallback.
//                       DEFAULT CHOICE MADE HERE: shown with a muted badge,
//                       not hidden. Silently hiding these would quietly
//                       reduce the page's apparent data completeness vs
//                       peers, and "unexplained missing ticker" is worse for
//                       trust than "ticker unresolved, here's why." Owner can
//                       override this default by passing hideUnresolved.
//
// Unknown/future status values render nothing rather than guessing at a
// badge for a state this component doesn't know about yet — fail-safe, not
// fail-loud, since a wrong badge is worse than no badge.
//
// Styling matches the existing badge language already used across
// ReviewConsoleTab.jsx / AdminPage.jsx / ReviewStatusPage.jsx (color+alpha
// background, 1px color+alpha border, 11px/700 weight, pill radius) —
// deliberately reused, not a new visual language for this one page.

import { AlertCircle, HelpCircle, Landmark, CircleDashed } from 'lucide-react';

const STATUS_CONFIG = {
  PENDING_REVIEW: {
    label: 'Ticker under review',
    color: '#f59e0b',
    icon: AlertCircle,
    tooltip: 'A candidate ticker match exists but has not been confirmed by manual review yet.',
  },
  UNRESOLVED: {
    label: 'Ticker unresolved',
    color: '#64748b',
    icon: HelpCircle,
    tooltip: 'No ticker could be matched for this disclosed asset.',
  },
  ROTATION_SIGNAL: {
    label: 'Safe-haven rotation',
    color: '#00d4ff',
    icon: Landmark,
    tooltip: 'A Treasury/bond position, not a missing ticker — this is the rotation signal itself.',
  },
  NON_EQUITY: {
    label: 'Non-equity holding',
    color: '#a78bfa',
    icon: CircleDashed,
    tooltip: 'Crypto or commodity holding — not trackable against equity ticker data.',
  },
};

/**
 * @param {string} status - row.tickerResolutionStatus from congressional_trades
 * @param {boolean} hideUnresolved - if true, renders nothing for UNRESOLVED
 *   instead of the muted badge (see header comment — default is to show it)
 */
export default function TickerStatusBadge({ status, hideUnresolved = false }) {
  if (!status || status === 'NATIVE') return null; // confirmed ticker — no badge, this is the common case
  if (status === 'UNRESOLVED' && hideUnresolved) return null;

  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null; // unknown/future status — render nothing rather than guess at a badge

  const Icon = cfg.icon;
  return (
    <span
      title={cfg.tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
        background: cfg.color + '1a', color: cfg.color, border: '1px solid ' + cfg.color + '44',
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}
